import React from "react";
import { SvgIcon } from "@mui/material";
import { SxProps, Theme } from "@mui/system";

/**
 * NOTE: Icon function should be used in conjunction with React Icons Remix
 * library, as well as the custom icons included in this file.
 */

interface Props {
  /**
   * Sets the icon size for the icon (defaults to `24px`)
   */
  size?: string;

  /**
   * Sets the fill color for the icon (defaults to `currentColor`)
   */
  color?: React.CSSProperties["color"];

  /**
   * Sets the secondary fill color where applicable, such as icons that contain an alert/notification (e.g. `AtNotificationFillAlert`).
   *
   * If no color is provided, it will default to the design default for that specific icon.
   */
  secondaryColor?: React.CSSProperties["color"];

  /**
   * Sets the text display number for icons that contain a count (e.g. `AtMultipleImages`).
   *
   * If no count is provided,  it will default to `1`
   */
  count?: number;

  /**
   * Set a native title for the icon
   */
  title?: string;

  className?: string;
}

export interface IconProps extends Props {
  /**
   * The name of the Element Icon to be rendered
   */
  icon:
    | React.ElementType
    | React.ReactElement
    | React.ReactChild
    | React.ReactFragment
    | React.ReactPortal;
  sx?: SxProps<Theme>;
}

/**
 * An Icon can be rendered using either the **Custom** Icon (prefixed with `At`) or **Remix** Icon (prefixed with `Ri`) libraries. Each icon should be rendered using the following pattern:
 *
 * `<Icon icon={AtCampus} />`
 */

const Icon: React.FC<IconProps> = ({
  color = "currentColor",
  size,
  icon,
  ...props
}) => {
  return (
    <SvgIcon
      style={{
        fill: color,
        fontSize: size,
        height: size,
        width: size,
      }}
      {...props}
      /* @ts-ignore */
      component={icon}
    />
  );
};

export default Icon;

// Armored Things Unique Icons are prefixed with "At"
export const AtActivityMap: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12,2A5.5,5.5,0,0,0,6.5,7.5C6.5,11.62,12,16,12,16s5.5-4.38,5.5-8.5A5.5,5.5,0,0,0,12,2Zm0,7a1.5,1.5,0,1,1,1.5-1.5A1.5,1.5,0,0,1,12,9Z" />
    <path d="M20,5V18l-5.17,1.91L9.56,17.79a1,1,0,0,0-.72,0L4,19.56V7H2V21a1,1,0,0,0,1.35.94l5.82-2.15,5.27,2.14a1,1,0,0,0,.72,0l6.19-2.29a1,1,0,0,0,.65-.94V5Z" />
  </svg>
);

export const AtAdmin: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M10.6977 13.8837V20.5814H4C4 18.8051 4.70565 17.1015 5.9617 15.8454C7.21776 14.5894 8.92134 13.8837 10.6977 13.8837ZM10.6977 13.0465C7.92233 13.0465 5.67442 10.7986 5.67442 8.02326C5.67442 5.24791 7.92233 3 10.6977 3C13.473 3 15.7209 5.24791 15.7209 8.02326C15.7209 10.7986 13.473 13.0465 10.6977 13.0465ZM12.8702 17.9124C12.7641 17.4657 12.7641 17.0003 12.8702 16.5536L12.0397 16.0739L12.8769 14.6238L13.7074 15.1035C14.0408 14.7878 14.4437 14.5549 14.8837 14.4237V13.4651H16.5581V14.4237C17.0035 14.556 17.4054 14.7921 17.7344 15.1035L18.5649 14.6238L19.4021 16.0739L18.5716 16.5536C18.6776 17 18.6776 17.4651 18.5716 17.9115L19.4021 18.3913L18.5649 19.8413L17.7344 19.3616C17.4011 19.6773 16.9981 19.9102 16.5581 20.0414V21H14.8837V20.0414C14.4437 19.9102 14.0408 19.6773 13.7074 19.3616L12.8769 19.8413L12.0397 18.3913L12.8702 17.9124ZM15.7209 16.3953C15.4989 16.3953 15.2859 16.4836 15.1289 16.6406C14.9719 16.7976 14.8837 17.0105 14.8837 17.2326C14.8837 17.4546 14.9719 17.6675 15.1289 17.8246C15.2859 17.9816 15.4989 18.0698 15.7209 18.0698C15.943 18.0698 16.1559 17.9816 16.3129 17.8246C16.4699 17.6675 16.5581 17.4546 16.5581 17.2326C16.5581 17.0105 16.4699 16.7976 16.3129 16.6406C16.1559 16.4836 15.943 16.3953 15.7209 16.3953Z" />
  </svg>
);

export const AtAddReport: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M18.56,12A6.57,6.57,0,0,1,13.31,1.5H1.5v21H19.88V11.87A7.2,7.2,0,0,1,18.56,12ZM6.75,19.88H4.12V9.38H6.75Zm3.94,0H8.06V12h2.63Zm3.93,0H12V14.62h2.62Z" />
    <path d="M17.25,1.5h2.63V4.12H22.5V6.75H19.88V9.38H17.25V6.75H14.62V4.12h2.63Z" />
  </svg>
);

export const AtSpaceManager: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path fill="none" d="M0 0h24v24H0z" />
    <path d="M5.33 15.929A13.064 13.064 0 0 1 5 13c0-5.088 2.903-9.436 7-11.182C16.097 3.564 19 7.912 19 13c0 1.01-.114 1.991-.33 2.929l2.02 1.796a.5.5 0 0 1 .097.63l-2.458 4.096a.5.5 0 0 1-.782.096l-2.254-2.254a1 1 0 0 0-.707-.293H9.414a1 1 0 0 0-.707.293l-2.254 2.254a.5.5 0 0 1-.782-.096l-2.458-4.095a.5.5 0 0 1 .097-.631l2.02-1.796zM12 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
  </svg>
);

export const AtAlertError: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <rect x="9" y="6" width="6" height="12" fill={secondaryColor || "none"} />
    <path d="M11.9999 22C6.47688 22 1.99988 17.523 1.99988 12C1.99988 6.477 6.47688 2 11.9999 2C17.5229 2 21.9999 6.477 21.9999 12C21.9999 17.523 17.5229 22 11.9999 22ZM10.9999 15V17H12.9999V15H10.9999ZM10.9999 7V13H12.9999V7H10.9999Z" />
  </svg>
);

export const AtEyeOpen: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M15.125 12C15.125 12.8288 14.7958 13.6237 14.2097 14.2097C13.6237 14.7958 12.8288 15.125 12 15.125C11.1712 15.125 10.3763 14.7958 9.79029 14.2097C9.20424 13.6237 8.875 12.8288 8.875 12C8.875 11.1712 9.20424 10.3763 9.79029 9.79029C10.3763 9.20424 11.1712 8.875 12 8.875C12.8288 8.875 13.6237 9.20424 14.2097 9.79029C14.7958 10.3763 15.125 11.1712 15.125 12Z" />
    <path d="M2 12C2 12 5.75 5.125 12 5.125C18.25 5.125 22 12 22 12C22 12 18.25 18.875 12 18.875C5.75 18.875 2 12 2 12ZM12 16.375C13.1603 16.375 14.2731 15.9141 15.0936 15.0936C15.9141 14.2731 16.375 13.1603 16.375 12C16.375 10.8397 15.9141 9.72688 15.0936 8.90641C14.2731 8.08594 13.1603 7.625 12 7.625C10.8397 7.625 9.72688 8.08594 8.90641 8.90641C8.08594 9.72688 7.625 10.8397 7.625 12C7.625 13.1603 8.08594 14.2731 8.90641 15.0936C9.72688 15.9141 10.8397 16.375 12 16.375Z" />
  </svg>
);

export const AtGlobe: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M2 12C2 9.34784 3.05357 6.8043 4.92893 4.92893C6.8043 3.05357 9.34784 2 12 2C14.6522 2 17.1957 3.05357 19.0711 4.92893C20.9464 6.8043 22 9.34784 22 12C22 14.6522 20.9464 17.1957 19.0711 19.0711C17.1957 20.9464 14.6522 22 12 22C9.34784 22 6.8043 20.9464 4.92893 19.0711C3.05357 17.1957 2 14.6522 2 12V12ZM11.375 3.34625C10.5375 3.60125 9.70625 4.37125 9.01625 5.665C8.78892 6.09512 8.59339 6.54131 8.43125 7H11.375V3.34625ZM7.1125 7C7.32084 6.33602 7.58859 5.69217 7.9125 5.07625C8.12863 4.66757 8.37824 4.27749 8.65875 3.91C7.11 4.55228 5.77751 5.62453 4.81875 7H7.1125ZM6.385 11.375C6.4225 10.2788 6.5575 9.2275 6.775 8.25H4.0925C3.6264 9.23131 3.34827 10.2913 3.2725 11.375H6.385ZM8.05875 8.25C7.81289 9.27475 7.67134 10.3218 7.63625 11.375H11.375V8.25H8.05875ZM12.625 8.25V11.375H16.3625C16.3279 10.3218 16.1867 9.27479 15.9412 8.25H12.625ZM7.6375 12.625C7.67218 13.6782 7.81332 14.7252 8.05875 15.75H11.375V12.625H7.6375ZM12.625 12.625V15.75H15.9412C16.175 14.7938 16.3238 13.74 16.3638 12.625H12.625ZM8.43125 17C8.60375 17.4825 8.8 17.93 9.01625 18.335C9.70625 19.6287 10.5387 20.3975 11.375 20.6538V17H8.43125ZM8.65875 20.09C8.37822 19.7225 8.12861 19.3324 7.9125 18.9237C7.5886 18.3078 7.32085 17.664 7.1125 17H4.81875C5.77746 18.3755 7.10997 19.4478 8.65875 20.09V20.09ZM6.775 15.75C6.54896 14.7226 6.4184 13.6765 6.385 12.625H3.2725C3.35 13.7375 3.63625 14.7912 4.0925 15.75H6.775ZM15.3413 20.09C16.89 19.4478 18.2225 18.3755 19.1812 17H16.8875C16.6791 17.664 16.4114 18.3078 16.0875 18.9237C15.8714 19.3325 15.6218 19.7226 15.3413 20.09V20.09ZM12.625 17V20.6538C13.4625 20.3988 14.2938 19.6287 14.9838 18.335C15.2 17.93 15.3962 17.4825 15.5687 17H12.625ZM17.225 15.75H19.9075C20.3638 14.7912 20.65 13.7375 20.7275 12.625H17.615C17.5816 13.6765 17.4511 14.7226 17.225 15.75ZM20.7275 11.375C20.6517 10.2913 20.3736 9.23132 19.9075 8.25H17.225C17.4425 9.2275 17.5775 10.2788 17.615 11.375H20.7275ZM16.0875 5.07625C16.3963 5.65625 16.665 6.30125 16.8875 7H19.1812C18.2225 5.62448 16.89 4.55222 15.3413 3.91C15.6138 4.265 15.8638 4.6575 16.0875 5.07625V5.07625ZM15.5687 7C15.4066 6.54131 15.2111 6.09511 14.9838 5.665C14.2938 4.37125 13.4625 3.6025 12.625 3.34625V7H15.5687Z" />
  </svg>
);

export const AtMoveLeft: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.7071 4.29289C13.0976 4.68342 13.0976 5.31658 12.7071 5.70711L7.41421 11H19C19.5523 11 20 11.4477 20 12C20 12.5523 19.5523 13 19 13H7.41421L12.7071 18.2929C13.0976 18.6834 13.0976 19.3166 12.7071 19.7071C12.3166 20.0976 11.6834 20.0976 11.2929 19.7071L4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929L11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289Z"
    />
  </svg>
);

export const AtMoveRight: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.2929 19.7071C10.9024 19.3166 10.9024 18.6834 11.2929 18.2929L16.5858 13L5 13C4.44771 13 4 12.5523 4 12C4 11.4477 4.44771 11 5 11L16.5858 11L11.2929 5.70711C10.9024 5.31658 10.9024 4.68342 11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289L19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071L12.7071 19.7071C12.3166 20.0976 11.6834 20.0976 11.2929 19.7071Z"
    />
  </svg>
);

export const AtMoveUp: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.7071 12.7071C19.3166 13.0976 18.6834 13.0976 18.2929 12.7071L13 7.41421L13 19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19L11 7.41421L5.70711 12.7071C5.31658 13.0976 4.68342 13.0976 4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929L11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289L19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071Z"
    />
  </svg>
);

export const AtMoveDown: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.29289 11.2929C4.68342 10.9024 5.31658 10.9024 5.70711 11.2929L11 16.5858V5C11 4.44771 11.4477 4 12 4C12.5523 4 13 4.44771 13 5L13 16.5858L18.2929 11.2929C18.6834 10.9024 19.3166 10.9024 19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071L12.7071 19.7071C12.3166 20.0976 11.6834 20.0976 11.2929 19.7071L4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929Z"
    />
  </svg>
);

export const AtRotateLeft: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M7.1001 18.32C8.2601 19.22 9.6101 19.76 11.0001 19.93V17.9C10.1301 17.75 9.2901 17.41 8.5401 16.87L7.1001 18.32ZM13.0001 4.07V1L8.4501 5.55L13.0001 10V6.09C15.8401 6.57 18.0001 9.03 18.0001 12C18.0001 14.97 15.8401 17.43 13.0001 17.91V19.93C16.9501 19.44 20.0001 16.08 20.0001 12C20.0001 7.92 16.9501 4.56 13.0001 4.07Z" />
  </svg>
);

export const AtRotateRight: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M15.55 5.55L11 1V4.07C7.06 4.56 4 7.92 4 12C4 16.08 7.05 19.44 11 19.93V17.91C8.16 17.43 6 14.97 6 12C6 9.03 8.16 6.57 11 6.09V10L15.55 5.55ZM13 17.9V19.92C14.39 19.75 15.74 19.21 16.9 18.31L15.46 16.87C14.71 17.41 13.87 17.76 13 17.9Z" />
  </svg>
);

export const AtScaleUp: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="m21 15.344-2.121 2.121-3.172-3.172-1.414 1.414 3.172 3.172L15.344 21H21zM3 8.656l2.121-2.121 3.172 3.172 1.414-1.414-3.172-3.172L8.656 3H3zM21 3h-5.656l2.121 2.121-3.172 3.172 1.414 1.414 3.172-3.172L21 8.656zM3 21h5.656l-2.121-2.121 3.172-3.172-1.414-1.414-3.172 3.172L3 15.344z" />
  </svg>
);

export const AtScaleDown: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M16.121 6.465 14 4.344V10h5.656l-2.121-2.121 3.172-3.172-1.414-1.414zM4.707 3.293 3.293 4.707l3.172 3.172L4.344 10H10V4.344L7.879 6.465zM19.656 14H14v5.656l2.121-2.121 3.172 3.172 1.414-1.414-3.172-3.172zM6.465 16.121l-3.172 3.172 1.414 1.414 3.172-3.172L10 19.656V14H4.344z" />
  </svg>
);

export const AtClose: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
  </svg>
);

export const AtConfigure: React.FC<Props> = ({ secondaryColor, ...props }) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M10.454 3.20875C11.803 2.92588 13.1958 2.9252 14.545 3.20675C14.6349 3.80004 14.8574 4.36544 15.1958 4.86093C15.5343 5.35642 15.9801 5.76925 16.5 6.06875C17.0196 6.36928 17.6002 6.54881 18.1987 6.594C18.7973 6.63919 19.3983 6.54887 19.957 6.32975C20.8753 7.35781 21.5709 8.56479 22 9.87475C21.5314 10.2493 21.1532 10.7247 20.8935 11.2655C20.6338 11.8064 20.4993 12.3988 20.5 12.9987C20.5 14.2627 21.086 15.3897 22.002 16.1227C21.5704 17.4316 20.8739 18.6376 19.956 19.6658C19.3974 19.4468 18.7966 19.3566 18.1983 19.4017C17.5999 19.4469 17.0195 19.6264 16.5 19.9268C15.9805 20.2262 15.5351 20.6387 15.1968 21.1338C14.8585 21.629 14.6361 22.1939 14.546 22.7868C13.1973 23.0703 11.8044 23.0716 10.455 22.7908C10.3656 22.1969 10.1434 21.6309 9.80487 21.1349C9.46638 20.6389 9.02038 20.2256 8.50005 19.9258C7.98041 19.6254 7.39975 19.446 6.80123 19.401C6.20272 19.356 5.60176 19.4465 5.04305 19.6658C4.12464 18.6374 3.42905 17.4301 3.00005 16.1198C3.46844 15.7454 3.84651 15.2703 4.10618 14.7299C4.36586 14.1894 4.50048 13.5974 4.50005 12.9977C4.50054 12.3976 4.36575 11.8051 4.10571 11.2643C3.84568 10.7234 3.46707 10.2481 2.99805 9.87375C3.42972 8.56487 4.12616 7.35885 5.04405 6.33075C5.6027 6.54971 6.20352 6.63995 6.80184 6.59476C7.40017 6.54958 7.98061 6.37013 8.50005 6.06975C9.01959 5.77035 9.46503 5.35777 9.80331 4.86266C10.1416 4.36754 10.364 3.80259 10.454 3.20975V3.20875ZM12.5 15.9987C13.2957 15.9987 14.0588 15.6827 14.6214 15.1201C15.184 14.5575 15.5 13.7944 15.5 12.9987C15.5 12.2031 15.184 11.44 14.6214 10.8774C14.0588 10.3148 13.2957 9.99875 12.5 9.99875C11.7044 9.99875 10.9413 10.3148 10.3787 10.8774C9.81612 11.44 9.50005 12.2031 9.50005 12.9987C9.50005 13.7944 9.81612 14.5575 10.3787 15.1201C10.9413 15.6827 11.7044 15.9987 12.5 15.9987V15.9987Z" />
  </svg>
);

export const AtAlertErrorStylized: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 32 32" {...props}>
    <rect
      x="9.75"
      y="4.5"
      width="4.5"
      height="15"
      fill={secondaryColor || "none"}
    />
    <path d="M0.75 12C0.75 5.79703 5.79703 0.75 12 0.75C18.203 0.75 23.25 5.79703 23.25 12C23.25 18.203 18.203 23.25 12 23.25C5.79703 23.25 0.75 18.203 0.75 12ZM12.7031 14.1094L13.3563 5.67188H10.6437L11.2969 14.1094H12.7031ZM10.5938 16.9219C10.5938 17.6988 11.223 18.3281 12 18.3281C12.777 18.3281 13.4062 17.6988 13.4062 16.9219C13.4062 16.1449 12.777 15.5156 12 15.5156C11.223 15.5156 10.5938 16.1449 10.5938 16.9219Z" />
  </svg>
);

export const AtAlertWarning: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <rect x="9" y="8" width="6" height="11" fill={secondaryColor || "none"} />
    <path d="M12.866 2.99996L22.392 19.5C22.4797 19.652 22.5259 19.8244 22.5259 19.9999C22.5259 20.1755 22.4797 20.3479 22.392 20.4999C22.3042 20.652 22.178 20.7782 22.026 20.866C21.874 20.9537 21.7015 20.9999 21.526 21H2.47397C2.29844 20.9999 2.126 20.9537 1.97398 20.866C1.82197 20.7782 1.69573 20.652 1.60797 20.4999C1.5202 20.3479 1.474 20.1755 1.474 19.9999C1.474 19.8244 1.52021 19.652 1.60797 19.5L11.134 2.99996C11.2217 2.84795 11.348 2.72172 11.5 2.63396C11.652 2.5462 11.8244 2.5 12 2.5C12.1755 2.5 12.3479 2.5462 12.5 2.63396C12.652 2.72172 12.7782 2.84795 12.866 2.99996ZM11 16V18H13V16H11ZM11 8.99996V14H13V8.99996H11Z" />
  </svg>
);

export const AtAlertWarningStylized: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <rect
      x="9.75"
      y="7.5"
      width="4.5"
      height="13.5"
      fill={secondaryColor || "none"}
    />
    <path d="M13.8887 2.62239L22.9948 19.4437C23.1702 19.7695 23.2579 20.1354 23.2494 20.5053C23.2409 20.8753 23.1365 21.2367 22.9463 21.5542C22.7561 21.8716 22.4866 22.1342 22.1644 22.3163C21.8422 22.4983 21.4782 22.5934 21.1082 22.5925H2.896C2.52439 22.5945 2.15874 22.4991 1.83549 22.3157C1.51224 22.1324 1.24269 21.8675 1.05369 21.5476C0.8628 21.2314 0.758242 20.8707 0.750468 20.5015C0.742694 20.1323 0.831977 19.7676 1.00939 19.4437L10.1155 2.62239C10.2998 2.28285 10.5724 1.99936 10.9045 1.80181C11.2365 1.60427 11.6157 1.5 12.0021 1.5C12.3884 1.5 12.7677 1.60427 13.0997 1.80181C13.4317 1.99936 13.7043 2.28285 13.8887 2.62239ZM13.3015 8.52904H10.8911L11.3931 15.5608L12.8 15.5608L13.3015 8.52904ZM11.4017 19.5691C11.5953 19.6985 11.821 19.771 12.0532 19.779C12.0677 19.7796 12.0822 19.7798 12.0968 19.7798C12.4284 19.7798 12.7464 19.6481 12.9809 19.4136C13.2153 19.1792 13.3471 18.8612 13.3471 18.5296C13.3471 18.2823 13.2737 18.0406 13.1364 17.835C12.999 17.6294 12.8037 17.4691 12.5753 17.3745C12.4351 17.3164 12.2863 17.2847 12.1362 17.2799C12.0415 17.2769 11.9463 17.2847 11.8524 17.3033C11.6099 17.3516 11.3871 17.4706 11.2123 17.6455C11.0374 17.8203 10.9183 18.0431 10.8701 18.2856C10.8218 18.5282 10.8466 18.7795 10.9412 19.008C11.0359 19.2364 11.1961 19.4317 11.4017 19.5691Z" />
  </svg>
);

export const AtAlertSuccess: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <rect
      x="4.5"
      y="6"
      width="15"
      height="10.5"
      fill={secondaryColor || "none"}
    />
    <path d="M11.9999 22C6.47688 22 1.99988 17.523 1.99988 12C1.99988 6.477 6.47688 2 11.9999 2C17.5229 2 21.9999 6.477 21.9999 12C21.9999 17.523 17.5229 22 11.9999 22ZM11.0029 16L18.0729 8.929L16.6589 7.515L11.0029 13.172L8.17388 10.343L6.75988 11.757L11.0029 16Z" />
  </svg>
);

export const AtArea: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17.91,1.5a4.65,4.65,0,0,0-4.6,4.59,4.6,4.6,0,1,0,9.19,0A4.65,4.65,0,0,0,17.91,1.5Z" />
    <path d="M11.34,14.62a2,2,0,1,0-2-2A2,2,0,0,0,11.34,14.62Z" />
    <path d="M11.32,22.23a9.56,9.56,0,0,0,9.56-9.3,7.1,7.1,0,0,1-2.05.52,7.54,7.54,0,1,1-8.28-8.28,7.1,7.1,0,0,1,.52-2,9.56,9.56,0,0,0,.25,19.11Z" />
  </svg>
);

export const AtSort: React.FC<Props> = () => (
  <svg viewBox="0 0 24 24" className={"AtSort"} width={"12px"} height={"12px"}>
    <path
      d="M18.7929 8C19.2383 8 19.4614 7.46143 19.1464 7.14645L12.3536 0.353553C12.1583 0.158291 11.8417 0.158291 11.6464 0.353553L4.85355 7.14645C4.53857 7.46143 4.76165 8 5.20711 8H18.7929Z"
      fill={""}
    />
    <path
      d="M5.20711 16C4.76165 16 4.53857 16.5386 4.85355 16.8536L11.6464 23.6464C11.8417 23.8417 12.1583 23.8417 12.3536 23.6464L19.1464 16.8536C19.4614 16.5386 19.2383 16 18.7929 16H5.20711Z"
      fill={""}
    />
  </svg>
);

export const AtDotsMenu: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M15 3C15 4.65685 13.6569 6 12 6C10.3431 6 9 4.65685 9 3C9 1.34315 10.3431 0 12 0C13.6569 0 15 1.34315 15 3Z" />
    <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" />
    <path d="M15 21C15 22.6569 13.6569 24 12 24C10.3431 24 9 22.6569 9 21C9 19.3431 10.3431 18 12 18C13.6569 18 15 19.3431 15 21Z" />
  </svg>
);

export const AtDownloadPDF: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.9989 13V18.585L14.8269 16.757L16.2419 18.172L11.9989 22.414L7.75592 18.172L9.17092 16.757L10.9989 18.585V13H12.9989ZM11.9989 2C13.7159 2.00008 15.373 2.63111 16.6551 3.77312C17.9372 4.91512 18.755 6.48846 18.9529 8.194C20.1972 8.53332 21.2826 9.2991 22.0196 10.3575C22.7565 11.416 23.0981 12.6997 22.9845 13.9844C22.871 15.2691 22.3096 16.473 21.3985 17.3858C20.4873 18.2986 19.2844 18.8622 17.9999 18.978L17.9989 17C18.0005 15.4271 17.3844 13.9166 16.2833 12.7935C15.1821 11.6703 13.684 11.0245 12.1114 10.995C10.5389 10.9655 9.01758 11.5547 7.8751 12.6357C6.73263 13.7168 6.06032 15.2032 6.00292 16.775L5.99892 17V18.978C4.71437 18.8623 3.51132 18.2989 2.60005 17.3862C1.68878 16.4735 1.12724 15.2696 1.01359 13.9848C0.899939 12.7001 1.2414 11.4163 1.97829 10.3578C2.71518 9.29926 3.80062 8.53339 5.04492 8.194C5.24262 6.48838 6.06032 4.91491 7.3425 3.77287C8.62467 2.63082 10.2819 1.99986 11.9989 2V2Z" />
  </svg>
);

export const AtExport: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M10.2222 6.66667V8.44444H5.77778V18.2222H15.5556V13.7778H17.3333V19.1111C17.3333 19.3469 17.2397 19.573 17.073 19.7397C16.9063 19.9064 16.6802 20 16.4444 20H4.88889C4.65314 20 4.42705 19.9064 4.26035 19.7397C4.09365 19.573 4 19.3469 4 19.1111V7.55556C4 7.31981 4.09365 7.09372 4.26035 6.92702C4.42705 6.76032 4.65314 6.66667 4.88889 6.66667H10.2222ZM20 4V12L16.6276 8.62844L11.2951 13.9618L10.0382 12.7049L15.3707 7.37156L12 4H20Z" />
  </svg>
);

export const AtExportCSV: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      d="M4.99787498,8.99999999 L4.99787498,0.999999992 L19.4999998,0.999999992 L22.9999998,4.50000005 L23,23 L4,23 M18,1 L18,6 L23,6 M7,13 C7,13 6.00000004,13 5,13 C3.99999996,13 3,13.5 3,14.5 L3,16 C3,16 3.00000001,16.5 3,17.5 C2.99999999,18.5 4,19 5,19 L7,19 M13.25,13 C13.25,13 12.25,13 10.75,13 C9.25,13 8.75,13.5 8.75,14.5 C8.75,15.5 9.25,16 10.75,16 C12.25,16 12.75,16.5 12.75,17.5 C12.75,18.5 12.25,19 10.75,19 C9.25,19 8.25,19 8.25,19 M20.5,12 C20.5,12 20.5,12 20.5,12.5 C20.5,13 18,19 18,19 L17.5,19 C17.5,19 15,13 15,12.5 L15,12"
    />
  </svg>
);

export const AtExportXLS: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      d="M4.99787498,8.99999999 L4.99787498,0.999999992 L19.4999998,0.999999992 L22.9999998,4.50000005 L23,23 L4,23 M18,1 L18,6 L23,6 M9.25,12 L7.25,15.25 L5.25,12 L5,12 L7.25,15.5 L4.75,19 L5,19 L7.25,15.75 L9.5,19 L9.75,19 L7.25,15.5 L9.5,12 L9.25,12 Z"
    />
  </svg>
);

export const AtExportPDF: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      d="M4.99787498,8.99999999 L4.99787498,0.999999992 L19.4999998,0.999999992 L22.9999998,4.50000005 L23,23 L4,23 M18,1 L18,6 L23,6 M3,12 L3.24999995,12 L4.49999995,12 C6.5,12 6.75,13.25 6.75,14 C6.75,14.75 6.5,16 4.49999995,16 L3.24999995,16 L3.24999995,18 L3,17.9999999 L3,12 Z M9.5,18 L9.5,12 C9.5,12 10.4473684,12 11.2052633,12 C12.3421053,12 13.5,12.5 13.5,15 C13.5,17.5 12.3421053,18 11.2052633,18 C10.4473684,18 9.5,18 9.5,18 Z M16.5,19 L16.5,12 L20.5,12 M16.5,15.5 L19.5,15.5"
    />
  </svg>
);

// Arrows

export const AtArrowUp: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.9997 7.828V20H10.9997V7.828L5.63568 13.192L4.22168 11.778L11.9997 4L19.7777 11.778L18.3637 13.192L12.9997 7.828Z" />
  </svg>
);
export const AtArrowRight: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M16.172 11.0007L10.808 5.63666L12.222 4.22266L20 12.0007L12.222 19.7787L10.808 18.3647L16.172 13.0007H4V11.0007H16.172Z" />
  </svg>
);

export const AtArrowCaretDoubleDown: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M16.59,5.1l-4.95,5-4.95-5L5.27,6.51l6.37,6.37L18,6.51Zm0,6-4.95,5-4.95-5L5.27,12.51l6.37,6.37L18,12.51Z" />
  </svg>
);

export const AtArrowCaretDoubleLeft: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.9,16.59,8,11.64l5-4.95L11.49,5.27,5.12,11.64,11.49,18Zm6,0L14,11.64,18.9,6.69,17.49,5.27l-6.37,6.37L17.49,18Z" />
  </svg>
);

export const AtArrowCaretDoubleRight: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M5.1,7.41l5,4.95-5,4.95,1.41,1.42,6.37-6.37L6.51,6Zm6,0,5,4.95-5,4.95,1.41,1.42,6.37-6.37L12.51,6Z" />
  </svg>
);

export const AtArrowCaretDoubleUp: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M7.41,18.9,12.36,14l4.95,4.95,1.42-1.41-6.37-6.37L6,17.49Zm0-6L12.36,8l4.95,5,1.42-1.41L12.36,5.12,6,11.49Z" />
  </svg>
);

export const AtArrowCollapseDown: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M3,8.25v-2H21v2Zm9,10.5,6-7.5H6Z" />
  </svg>
);

export const AtArrowCollapseLeft: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M15.75,3h2V21h-2ZM5.25,12l7.5,6V6Z" />
  </svg>
);

export const AtArrowExpandRight: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M8.25,21h-2V3h2Zm10.5-9-7.5-6V18Z" />
  </svg>
);

export const AtArrowExpandUp: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M21,15.75v2H3v-2ZM12,5.25l-6,7.5H18Z" />
  </svg>
);

export const AtAssets: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17,8v2h2v9H15V2L3,6V21H21V8Zm-6,7H7V13h4Zm0-4H7V9h4Z" />
  </svg>
);

export const AtCompare: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 8.37308V6.56382H16.9239C16.9468 6.56405 16.9679 6.56449 16.9874 6.56512C16.9923 6.60788 16.9966 6.65794 17 6.71639V16.5527C15.8348 16.9645 15 18.0758 15 19.382C15 21.0389 16.3431 22.382 18 22.382C19.6569 22.382 21 21.0389 21 19.382C21 18.0758 20.1652 16.9645 19 16.5527V6.66382L18.9987 6.63832C18.9737 6.14943 18.8888 5.51882 18.4274 5.067C17.9536 4.60307 17.3282 4.56727 16.9373 4.56386L16.9286 4.56382H14V2.5L9.03047 5.85605L14 8.37308ZM17 19.382C17 18.8297 17.4477 18.382 18 18.382C18.5523 18.382 19 18.8297 19 19.382C19 19.9343 18.5523 20.382 18 20.382C17.4477 20.382 17 19.9343 17 19.382Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7 9.21129C8.16519 8.79946 9 7.68822 9 6.382C9 4.72514 7.65685 3.382 6 3.382C4.34315 3.382 3 4.72514 3 6.382C3 7.68822 3.83481 8.79946 5 9.21129V19.1002L5.0013 19.1257C5.02629 19.6146 5.11123 20.2452 5.5726 20.697C6.04635 21.1609 6.6718 21.1967 7.0627 21.2001L7.07143 21.2002H10V23.264L14.9695 19.908L10 17.3909V19.2002H7.07607C7.05318 19.2 7.03207 19.1995 7.01258 19.1989C7.00768 19.1561 7.00336 19.1061 7 19.0476V9.21129ZM7 6.382C7 6.93428 6.55228 7.382 6 7.382C5.44772 7.382 5 6.93428 5 6.382C5 5.82971 5.44772 5.382 6 5.382C6.55228 5.382 7 5.82971 7 6.382Z"
    />
  </svg>
);

export const AtShare: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="12" />
    <path d="M11.6667 7V10C7.98467 10 5 12.9847 5 16.6667C5 16.8487 5.00667 17.0287 5.02133 17.2067C5.99667 15.3573 7.908 14.0793 10.1247 14.0033L10.3333 14H11.6667V17L17 12L11.6667 7ZM15.6667 7V8.82467L19.0533 12L15.6673 15.174L15.6667 17L21 12L15.6667 7Z" />
  </svg>
);

export const AtToggle: React.FC<Props> = (props) => (
  // <svg viewBox="0 0 24 24" {...props}>
  //   <circle cx="12" cy="12" r="12" />
  //   <path d="M4.5 9a3.5 3.5 0 1 0 0 7h7a3.5 3.5 0 1 0 0-7h-7zm7 6a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm-7-14a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm2.45 0A3.49 3.49 0 0 1 8 3.5 3.49 3.49 0 0 1 6.95 6h4.55a2.5 2.5 0 0 0 0-5H6.95zM4.5 0h7a3.5 3.5 0 1 1 0 7h-7a3.5 3.5 0 1 1 0-7z" />
  // </svg>
  <svg viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="12" />
    <path d="M9.25 12.7814C8.52065 12.7814 7.82118 13.0712 7.30546 13.5869C6.78973 14.1026 6.5 14.8021 6.5 15.5314C6.5 16.2608 6.78973 16.9602 7.30546 17.476C7.82118 17.9917 8.52065 18.2814 9.25 18.2814H14.75C15.4793 18.2814 16.1788 17.9917 16.6945 17.476C17.2103 16.9602 17.5 16.2608 17.5 15.5314C17.5 14.8021 17.2103 14.1026 16.6945 13.5869C16.1788 13.0712 15.4793 12.7814 14.75 12.7814H9.25ZM14.75 17.4957C14.229 17.4957 13.7294 17.2888 13.361 16.9204C12.9927 16.552 12.7857 16.0524 12.7857 15.5314C12.7857 15.0105 12.9927 14.5108 13.361 14.1425C13.7294 13.7741 14.229 13.5671 14.75 13.5671C15.271 13.5671 15.7706 13.7741 16.139 14.1425C16.5073 14.5108 16.7143 15.0105 16.7143 15.5314C16.7143 16.0524 16.5073 16.552 16.139 16.9204C15.7706 17.2888 15.271 17.4957 14.75 17.4957V17.4957ZM9.25 6.49571C8.72904 6.49571 8.22942 6.70266 7.86104 7.07104C7.49267 7.43941 7.28571 7.93904 7.28571 8.46C7.28571 8.98096 7.49267 9.48058 7.86104 9.84896C8.22942 10.2173 8.72904 10.4243 9.25 10.4243C9.77096 10.4243 10.2706 10.2173 10.639 9.84896C11.0073 9.48058 11.2143 8.98096 11.2143 8.46C11.2143 7.93904 11.0073 7.43941 10.639 7.07104C10.2706 6.70266 9.77096 6.49571 9.25 6.49571V6.49571ZM11.175 6.49571C11.4366 6.75153 11.6444 7.05711 11.7861 7.39446C11.9278 7.73181 12.0005 8.0941 12 8.46C12.0005 8.8259 11.9278 9.18819 11.7861 9.52554C11.6444 9.86289 11.4366 10.1685 11.175 10.4243H14.75C15.271 10.4243 15.7706 10.2173 16.139 9.84896C16.5073 9.48058 16.7143 8.98096 16.7143 8.46C16.7143 7.93904 16.5073 7.43941 16.139 7.07104C15.7706 6.70266 15.271 6.49571 14.75 6.49571H11.175ZM9.25 5.71H14.75C15.4793 5.71 16.1788 5.99973 16.6945 6.51546C17.2103 7.03118 17.5 7.73065 17.5 8.46C17.5 9.18934 17.2103 9.88882 16.6945 10.4045C16.1788 10.9203 15.4793 11.21 14.75 11.21H9.25C8.52065 11.21 7.82118 10.9203 7.30546 10.4045C6.78973 9.88882 6.5 9.18934 6.5 8.46C6.5 7.73065 6.78973 7.03118 7.30546 6.51546C7.82118 5.99973 8.52065 5.71 9.25 5.71V5.71Z" />
  </svg>
);

export const AtAverage: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M18.22,7.58a5.75,5.75,0,0,0-2.5-2.71l1-2.9L14.89,1.5l-.84,2.77a6.7,6.7,0,0,0-1.49-.15,6.29,6.29,0,0,0-3.43,1A6.32,6.32,0,0,0,6.81,7.8,10.33,10.33,0,0,0,6,12.07a10.13,10.13,0,0,0,.88,4.44,6.09,6.09,0,0,0,2.5,2.68L8.48,22l1.78.47.79-2.71a6.6,6.6,0,0,0,1.51.16A6.19,6.19,0,0,0,16,19a6.07,6.07,0,0,0,2.3-2.7A10,10,0,0,0,19.12,12,10,10,0,0,0,18.22,7.58Zm-8.09,10c-1.24-.91-1.85-2.73-1.85-5.46A7.86,7.86,0,0,1,9.41,7.36a3.69,3.69,0,0,1,3.15-1.51A6.73,6.73,0,0,1,13.69,6Zm2.43.69a5.5,5.5,0,0,1-1.13-.12L15,6.49a3.85,3.85,0,0,1,1.42,2A10.9,10.9,0,0,1,16.85,12Q16.85,18.22,12.56,18.22Z" />
  </svg>
);

export const AtOccupancy: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M2 22C2 19.8783 2.84285 17.8434 4.34315 16.3431C5.84344 14.8429 7.87827 14 10 14C12.1217 14 14.1566 14.8429 15.6569 16.3431C17.1571 17.8434 18 19.8783 18 22H2ZM10 13C6.685 13 4 10.315 4 7C4 3.685 6.685 1 10 1C13.315 1 16 3.685 16 7C16 10.315 13.315 13 10 13ZM17.363 15.233C18.8926 15.6261 20.2593 16.4918 21.2683 17.7068C22.2774 18.9218 22.8774 20.4242 22.983 22H20C20 19.39 19 17.014 17.363 15.233ZM15.34 12.957C16.178 12.2075 16.8482 11.2893 17.3066 10.2627C17.765 9.23616 18.0013 8.12429 18 7C18.0021 5.63347 17.6526 4.28937 16.985 3.097C18.1176 3.32459 19.1365 3.93737 19.8685 4.8312C20.6004 5.72502 21.0002 6.84473 21 8C21.0003 8.71247 20.8482 9.41676 20.5541 10.0657C20.26 10.7146 19.8305 11.2931 19.2946 11.7625C18.7586 12.2319 18.1285 12.5814 17.4464 12.7874C16.7644 12.9934 16.0462 13.0512 15.34 12.957V12.957Z" />
  </svg>
);

export const AtTableColumn: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M18.22,7.58a5.75,5.75,0,0,0-2.5-2.71l1-2.9L14.89,1.5l-.84,2.77a6.7,6.7,0,0,0-1.49-.15,6.29,6.29,0,0,0-3.43,1A6.32,6.32,0,0,0,6.81,7.8,10.33,10.33,0,0,0,6,12.07a10.13,10.13,0,0,0,.88,4.44,6.09,6.09,0,0,0,2.5,2.68L8.48,22l1.78.47.79-2.71a6.6,6.6,0,0,0,1.51.16A6.19,6.19,0,0,0,16,19a6.07,6.07,0,0,0,2.3-2.7A10,10,0,0,0,19.12,12,10,10,0,0,0,18.22,7.58Zm-8.09,10c-1.24-.91-1.85-2.73-1.85-5.46A7.86,7.86,0,0,1,9.41,7.36a3.69,3.69,0,0,1,3.15-1.51A6.73,6.73,0,0,1,13.69,6Zm2.43.69a5.5,5.5,0,0,1-1.13-.12L15,6.49a3.85,3.85,0,0,1,1.42,2A10.9,10.9,0,0,1,16.85,12Q16.85,18.22,12.56,18.22Z" />
  </svg>
);

export const AtBathroom: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M13,1.5H11v21h2Z" />
    <path d="M5.44,8.06A2.63,2.63,0,1,0,2.81,5.44,2.62,2.62,0,0,0,5.44,8.06Z" />
    <path d="M2.65,10.53a3.95,3.95,0,0,1,6.73,2.78v3.94H8.06v3.94H2.81V17.25H1.5V13.31A3.93,3.93,0,0,1,2.65,10.53Z" />
    <path d="M18.56,8.06a2.63,2.63,0,1,0-2.62-2.62A2.62,2.62,0,0,0,18.56,8.06Z" />
    <path d="M21.35,10.53a3.93,3.93,0,0,1,1.15,2.78v3.94H21.19v3.94H15.94V17.25H14.62V13.31a3.95,3.95,0,0,1,6.73-2.78Z" />
  </svg>
);

export const AtBuilding: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M18.56,12V1.5H5.44V12H3v9.75H21V12ZM14.62,18H9.38v-2h5.24Zm0-5.25H9.38v-2h5.24Zm0-5.25H9.38v-2h5.24Z" />
  </svg>
);

export const AtCampus: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M22.5,7.67V5.51l-10.81-4L1.5,5.44V7.67Z" />
    <path d="M6,9.47H3.34v8.36H6Z" />
    <path d="M22.5,19.89V22.5H1.5V19.89Z" />
    <path d="M10.86,9.47H8.24v8.36h2.62Z" />
    <path d="M13.14,9.47h2.62v8.36H13.14Z" />
    <path d="M20.66,9.47H18v8.36h2.63Z" />
  </svg>
);

export const AtCategory: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M19.5,4.85H4.5V2.25h15Z" />
    <path d="M2,7.45H22l-2.5,14.3H4.5Z" />
  </svg>
);

// export const AtCompare: React.FC<Props> = (props) => (
//   <svg viewBox="0 0 24 24" {...props}>
//     <path d="M9.56,1.5h2.63v21H9.56v-2H3V3.47H9.56Z" />
//     <path d="M14.16,17.91h4.59V6.09H14.16V3.47h7.22V20.53H14.16Z" />
//   </svg>
// );

export const AtLink: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17.3033 14.9456L16.125 13.7656L17.3033 12.5872C17.693 12.2009 18.0025 11.7415 18.2142 11.2352C18.4258 10.729 18.5354 10.1859 18.5366 9.6372C18.5378 9.08849 18.4306 8.54494 18.2212 8.03777C18.0118 7.5306 17.7042 7.06979 17.3162 6.68179C16.9282 6.2938 16.4674 5.98626 15.9603 5.77683C15.4531 5.5674 14.9095 5.46021 14.3608 5.46142C13.8121 5.46262 13.2691 5.57219 12.7628 5.78383C12.2566 5.99548 11.7971 6.30503 11.4108 6.69473L10.2325 7.87389L9.0533 6.69556L10.2333 5.51722C11.3273 4.42321 12.8111 3.80859 14.3583 3.80859C15.9055 3.80859 17.3893 4.42321 18.4833 5.51722C19.5773 6.61124 20.1919 8.09505 20.1919 9.64222C20.1919 11.1894 19.5773 12.6732 18.4833 13.7672L17.3033 14.9456ZM14.9466 17.3022L13.7675 18.4806C12.6735 19.5746 11.1896 20.1892 9.64247 20.1892C8.09529 20.1892 6.61149 19.5746 5.51747 18.4806C4.42345 17.3865 3.80884 15.9027 3.80884 14.3556C3.80884 12.8084 4.42345 11.3246 5.51747 10.2306L6.69664 9.05222L7.87497 10.2322L6.69664 11.4106C6.30694 11.7969 5.99739 12.2563 5.78574 12.7626C5.5741 13.2688 5.46453 13.8119 5.46333 14.3606C5.46212 14.9093 5.56931 15.4528 5.77874 15.96C5.98817 16.4672 6.29571 16.928 6.6837 17.316C7.0717 17.704 7.53251 18.0115 8.03968 18.221C8.54686 18.4304 9.0904 18.5376 9.63911 18.5364C10.1878 18.5352 10.7309 18.4256 11.2371 18.214C11.7434 18.0023 12.2028 17.6928 12.5891 17.3031L13.7675 16.1247L14.9466 17.3022ZM14.3566 8.46306L15.5358 9.64222L9.6433 15.5339L8.46414 14.3556L14.3566 8.46306Z" />
  </svg>
);

export const AtMetrics: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M19.375 15.1045C20.0239 13.5628 20.1733 11.8565 19.8021 10.2255C19.431 8.5945 18.558 7.12084 17.306 6.0117C16.0539 4.90256 14.4857 4.21372 12.8219 4.04201C11.158 3.87031 9.48222 4.22438 8.03004 5.05445L7.03804 3.31745C8.55567 2.45007 10.2742 1.99582 12.0222 2.00003C13.7703 2.00424 15.4866 2.46676 17 3.34145C21.49 5.93345 23.21 11.4835 21.117 16.1115L22.459 16.8855L18.294 19.0995L18.129 14.3855L19.375 15.1045V15.1045ZM4.62504 8.89845C3.97618 10.4401 3.82682 12.1464 4.19796 13.7774C4.56909 15.4084 5.44205 16.8821 6.69411 17.9912C7.94617 19.1003 9.51436 19.7892 11.1782 19.9609C12.842 20.1326 14.5179 19.7785 15.97 18.9485L16.962 20.6855C15.4444 21.5528 13.7258 22.0071 11.9778 22.0029C10.2298 21.9987 8.51347 21.5361 7.00004 20.6615C2.51004 18.0695 0.790039 12.5195 2.88304 7.89145L1.54004 7.11845L5.70504 4.90445L5.87004 9.61845L4.62404 8.89945L4.62504 8.89845ZM8.50004 14.0015H14C14.1326 14.0015 14.2598 13.9488 14.3536 13.855C14.4474 13.7612 14.5 13.6341 14.5 13.5015C14.5 13.3688 14.4474 13.2417 14.3536 13.1479C14.2598 13.0541 14.1326 13.0015 14 13.0015H10C9.337 13.0015 8.70111 12.7381 8.23227 12.2692C7.76343 11.8004 7.50004 11.1645 7.50004 10.5015C7.50004 9.83841 7.76343 9.20253 8.23227 8.73368C8.70111 8.26484 9.337 8.00145 10 8.00145H11V7.00145H13V8.00145H15.5V10.0015H10C9.86743 10.0015 9.74025 10.0541 9.64649 10.1479C9.55272 10.2417 9.50004 10.3688 9.50004 10.5015C9.50004 10.6341 9.55272 10.7612 9.64649 10.855C9.74025 10.9488 9.86743 11.0015 10 11.0015H14C14.6631 11.0015 15.299 11.2648 15.7678 11.7337C16.2366 12.2025 16.5 12.8384 16.5 13.5015C16.5 14.1645 16.2366 14.8004 15.7678 15.2692C15.299 15.7381 14.6631 16.0015 14 16.0015H13V17.0015H11V16.0015H8.50004V14.0015Z" />
  </svg>
);

export const AtEmpty: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M21,12a9,9,0,1,1-9-9A9,9,0,0,1,21,12Z" />
  </svg>
);

export const AtErrorWarningFillAlert: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      d="M24 8C24 11.3137 21.3137 14 18 14C14.6863 14 12 11.3137 12 8C12 4.68629 14.6863 2 18 2C21.3137 2 24 4.68629 24 8Z"
      fill={secondaryColor || "#ED3B3B"}
    />
    <path d="M11.844 4.00098C6.39292 4.08435 2 8.52889 2 13.9998C2 19.5228 6.477 23.9998 12 23.9998C17.4708 23.9998 21.9153 19.607 21.9988 14.156C20.8484 14.9049 19.475 15.3399 18 15.3399C16.0681 15.3399 14.3106 14.5936 13 13.3736V14.9998H11V10.2146C10.7791 9.51582 10.66 8.77179 10.66 7.99994C10.66 6.52488 11.0951 5.15144 11.844 4.00098ZM11 18.9998V16.9998H13V18.9998H11Z" />
    <text
      fill="white"
      xmlSpace="preserve"
      style={{ whiteSpace: "pre", textAlign: "center" }}
      fontFamily="Fira Sans"
      fontSize="10.5"
      fontWeight="600"
      letterSpacing="0em"
    >
      <tspan x="15.2981" y="11.5175">
        {props.count || 1}
      </tspan>
    </text>
  </svg>
);

export const AtEstimateCertainty: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M2.9,8.78a10.53,10.53,0,0,1,14.2,2.35l-3.61,3.61L9.42,10.68,1.93,18.17l1.43,1.42,6.06-6.07,4.07,4.07,4.7-4.7a10.43,10.43,0,0,1,1.07,4.63h2a12.53,12.53,0,0,0-1.59-6.12l2.65-2.66L20.91,7.32,18.53,9.7A12.54,12.54,0,0,0,1.78,7.11Z" />
  </svg>
);

export const AtFloor: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12,3,1.5,9.56,12,16.12,22.5,9.56Z" />
    <path d="M1.5,14.81,3.6,13.5,12,18.75l8.4-5.25,2.1,1.31L12,21.38Z" />
  </svg>
);

export const AtGroupBy: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2ZM10.41,15.41A2,2,0,1,1,11,14,2,2,0,0,1,10.41,15.41Zm.18-5A2,2,0,1,1,12,11,2,2,0,0,1,10.59,10.41Zm5.82,5A2,2,0,1,1,17,14,2,2,0,0,1,16.41,15.41Z" />
  </svg>
);

export const AtInfo: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M16.1003 5.55453C16.1003 6.63393 15.2253 7.50896 14.1459 7.50896C13.0665 7.50896 12.1915 6.63393 12.1915 5.55453C12.1915 4.47513 13.0665 3.6001 14.1459 3.6001C15.2253 3.6001 16.1003 4.47513 16.1003 5.55453Z" />
    <path d="M15.5133 19.9054L15.7525 18.9251C15.156 19.2065 13.9466 19.4254 13.4345 19.0119C12.9154 18.5928 13.2516 17.181 13.4361 16.5399L14.332 13.3706C14.4868 12.8546 14.5603 12.1088 14.5603 11.6828C14.5603 10.2052 13.415 9.42812 11.8569 9.42812C10.8179 9.42812 9.6054 9.90657 8.63991 10.299L8.3999 11.2794C8.833 11.1175 9.31145 10.9635 9.74377 10.9635C10.6295 10.9635 10.9555 11.1926 10.9555 12.0525C10.9555 12.5513 10.7992 13.1806 10.6717 13.6645C10.5234 14.1899 10.3886 14.6524 10.2679 15.0664C9.79945 16.6729 9.54442 17.5476 9.54442 18.5217C9.54442 19.1753 9.78599 19.7147 10.2691 20.1392C11.7622 21.4538 14.2644 20.4201 15.2847 19.9986C15.3723 19.9624 15.4491 19.9307 15.5133 19.9054Z" />
  </svg>
);

export const AtLayers: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M3,3H14.25V13.12H3Z" />
    <path d="M17.62,16.5V6.37H15.38v7.88h-9V16.5Z" />
    <path d="M21,9.75V19.88H9.75V17.63h9V9.75Z" />
  </svg>
);

export const AtLive: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <circle opacity="0.2" cx="12" cy="12" r="11.5714" fill="#22A437" />
    <circle cx="12" cy="12" r="5.14286" fill="#22A437" />
  </svg>
);

export const AtMenuRight: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M22 6H2V4H22V6ZM22 13H2V11H22V13ZM10 20L22 20V18L10 18V20Z" />
  </svg>
);

export const AtMultipleImages: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17 2H2V17H17V2ZM7 22H22V7H19.5V19.5H7V22Z" />
    <text
      fill={secondaryColor}
      xmlSpace="preserve"
      style={{ whiteSpace: "pre" }}
      fontFamily="Fira Sans"
      fontSize="12"
      fontWeight="600"
      letterSpacing="0em"
    >
      <tspan x="6.41211" y="13.52">
        {props.count || 1}
      </tspan>
    </text>
  </svg>
);

export const AtNotificationFillAlert: React.FC<Props> = ({
  secondaryColor,
  ...props
}) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.3778 2.11951C11.9259 2.04049 11.465 2 11 2C8.87827 2 6.84344 2.84285 5.34315 4.34315C3.84285 5.84344 3 7.87827 3 10V17H1V19H21V17H19V11.1589C18.7379 11.1916 18.4708 11.2084 18.1998 11.2084C14.6608 11.2084 11.7918 8.33942 11.7918 4.80038C11.7918 3.84325 12.0016 2.93513 12.3778 2.11951Z" />
    <path d="M14 23V21H8V23H14Z" />
    <path
      d="M22.9999 4.8C22.9999 7.45097 20.8509 9.6 18.1999 9.6C15.5489 9.6 13.3999 7.45097 13.3999 4.8C13.3999 2.14903 15.5489 0 18.1999 0C20.8509 0 22.9999 2.14903 22.9999 4.8Z"
      fill={secondaryColor || "#ED3B3B"}
    />
  </svg>
);

export const AtNotificationFillSnooze: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M22.5,0h-9V3h4.34L13.5,6.75v3h9v-3H18.16L22.5,3Z" />
    <path d="M10.5,2a8.22,8.22,0,0,1,1,.06V3L13,4.52l-.82.71-.7,1.52v3l2,2h5V17h2v2H.5V17h2V10a8,8,0,0,1,8-8Z" />
    <path d="M13.5,21v2h-6V21Z" />
  </svg>
);

export const AtProfile: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 4C7.58172 4 4 7.58172 4 12C4 13.892 4.65681 15.6306 5.75486 17.0003C7.22097 15.1712 9.47394 14 12.0005 14C14.5267 14 16.7795 15.171 18.2456 16.9997C19.3434 15.6302 20 13.8918 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM8.5 9.5C8.5 7.567 10.067 6 12 6C13.933 6 15.5 7.567 15.5 9.5C15.5 11.433 13.933 13 12 13C10.067 13 8.5 11.433 8.5 9.5Z" />
  </svg>
);

export const AtRefresh: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M19.7143 1.5V8H13.2857L15.7618 5.49635C15.2399 5.18187 14.6779 4.93193 14.0874 4.755C12.3921 4.24707 10.5726 4.37422 8.962 5.11371C7.35109 5.85332 6.05529 7.15689 5.31508 8.78785C4.57477 10.419 4.4409 12.2663 4.93865 13.9899C5.43635 15.7133 6.53129 17.1947 8.01947 18.1642C9.50737 19.1335 11.2896 19.527 13.0394 19.2741C14.7894 19.0213 16.3927 18.1384 17.5542 16.7846C18.7158 15.4305 19.3569 13.6964 19.3572 11.9002L21.3572 11.9006C21.3568 14.1718 20.5463 16.3684 19.0721 18.0868C17.5977 19.8055 15.5577 20.931 13.3254 21.2536C11.0931 21.5762 8.8214 21.0736 6.92776 19.84C5.0344 18.6065 3.64701 16.7257 3.01717 14.5448C2.38739 12.364 2.55643 10.0268 3.49387 7.96129C4.43142 5.89553 6.07566 4.23819 8.12749 3.29613C10.1796 2.35395 12.4998 2.19148 14.6614 2.83914C15.57 3.11138 16.4246 3.51894 17.1999 4.04232L19.7143 1.5ZM12.0001 12H15.75V14H10.0001V8.25H12.0001V12Z" />
  </svg>
);

export const AtRetailMarketing: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M20.87,19.93l-.17-.05L13.84,18v3.32H5.41V15.55l-1.57-.36h0L3,15V8.19l1.09-.3L20.7,3.15h0l.5-.15,0,17Zm-13-1.13h3.61V17.35L7.82,16.27Z" />
  </svg>
);

export const AtSchedule: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M21,4H17V2H15V4H9V2H7V4H2V22H22V4ZM20,20H4V8H20Z" />
    <rect x="7" y="11" width="2" height="2" />
    <rect x="7" y="15" width="2" height="2" />
    <rect x="11" y="11" width="2" height="2" />
    <rect x="11" y="15" width="2" height="2" />
    <rect x="15" y="11" width="2" height="2" />
  </svg>
);

export const AtSnapshots: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4 2H9V4H4V2ZM2 5H22L22 21H2V5ZM15 17C17.2091 17 19 15.2091 19 13C19 10.7909 17.2091 9 15 9C12.7909 9 11 10.7909 11 13C11 15.2091 12.7909 17 15 17ZM6.5 11C7.05228 11 7.5 10.5523 7.5 10C7.5 9.44772 7.05228 9 6.5 9C5.94772 9 5.5 9.44772 5.5 10C5.5 10.5523 5.94772 11 6.5 11Z" />
  </svg>
);

export const AtHelp: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <g clipPath="url(#clip0_4736_492295)">
      <path d="M21 8.00088C21.5304 8.00088 22.0391 8.21159 22.4142 8.58666C22.7893 8.96174 23 9.47044 23 10.0009V14.0009C23 14.5313 22.7893 15.04 22.4142 15.4151C22.0391 15.7902 21.5304 16.0009 21 16.0009H19.938C19.6942 17.9342 18.7533 19.7121 17.2917 21.0009C15.8302 22.2897 13.9486 23.0008 12 23.0009V21.0009C13.5913 21.0009 15.1174 20.3687 16.2426 19.2435C17.3679 18.1183 18 16.5922 18 15.0009V9.00088C18 7.40958 17.3679 5.88345 16.2426 4.75824C15.1174 3.63302 13.5913 3.00088 12 3.00088C10.4087 3.00088 8.88258 3.63302 7.75736 4.75824C6.63214 5.88345 6 7.40958 6 9.00088V16.0009H3C2.46957 16.0009 1.96086 15.7902 1.58579 15.4151C1.21071 15.04 1 14.5313 1 14.0009V10.0009C1 9.47044 1.21071 8.96174 1.58579 8.58666C1.96086 8.21159 2.46957 8.00088 3 8.00088H4.062C4.30603 6.06777 5.24708 4.29015 6.70857 3.00155C8.17007 1.71296 10.0516 1.00195 12 1.00195C13.9484 1.00195 15.8299 1.71296 17.2914 3.00155C18.7529 4.29015 19.694 6.06777 19.938 8.00088H21ZM7.76 15.7859L8.82 14.0899C9.77308 14.6869 10.8754 15.0027 12 15.0009C13.1246 15.0027 14.2269 14.6869 15.18 14.0899L16.24 15.7859C14.9693 16.582 13.4995 17.0032 12 17.0009C10.5005 17.0032 9.03074 16.582 7.76 15.7859Z" />
    </g>
    <defs>
      <clipPath id="clip0_4736_492295">
        <rect width="24" height="24" />
      </clipPath>
    </defs>
  </svg>
);

export const AtStickyNoteAdd: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M10.71,17.62A5.15,5.15,0,0,0,12,21H4a1,1,0,0,1-1-1V4A1,1,0,0,1,4,3H20a1,1,0,0,1,1,1v9.12A7.06,7.06,0,0,0,17.14,12C13.59,12,10.71,14.52,10.71,17.62Z" />
    <path d="M15.86,14.25h2.57V16.5H21v2.25H18.43V21H15.86V18.75H13.29V16.5h2.57Z" />
  </svg>
);

export const AtLocation: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.2427 10.5761L8 14.8188L3.75734 10.5761C2.91823 9.73699 2.34679 8.66789 2.11529 7.50401C1.88378 6.34013 2.0026 5.13373 2.45673 4.03738C2.91086 2.94103 3.6799 2.00396 4.66659 1.34467C5.65328 0.685388 6.81332 0.333496 8 0.333496C9.18669 0.333496 10.3467 0.685388 11.3334 1.34467C12.3201 2.00396 13.0891 2.94103 13.5433 4.03738C13.9974 5.13373 14.1162 6.34013 13.8847 7.50401C13.6532 8.66789 13.0818 9.73699 12.2427 10.5761ZM8 7.66678C8.35362 7.66678 8.69276 7.5263 8.94281 7.27625C9.19286 7.0262 9.33334 6.68707 9.33334 6.33344C9.33334 5.97982 9.19286 5.64068 8.94281 5.39064C8.69276 5.14059 8.35362 5.00011 8 5.00011C7.64638 5.00011 7.30724 5.14059 7.05719 5.39064C6.80715 5.64068 6.66667 5.97982 6.66667 6.33344C6.66667 6.68707 6.80715 7.0262 7.05719 7.27625C7.30724 7.5263 7.64638 7.66678 8 7.66678Z" />
  </svg>
);

export const AtTagNames: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M11.1913 16.2672L18.5015 8.95938L18.0562 3.69531L12.7921 3.25L5.48193 10.5602L11.1913 16.2672ZM13.9101 6.52891C13.9608 6.33825 14.061 6.16437 14.2005 6.02485C14.3401 5.88534 14.5139 5.78514 14.7046 5.73438C15.0937 5.63125 15.5062 5.74141 15.7921 6.025C15.9319 6.16468 16.0324 6.33878 16.0834 6.52972C16.1345 6.72066 16.1342 6.92169 16.0827 7.1125C16.032 7.30316 15.9317 7.47704 15.7922 7.61655C15.6527 7.75607 15.4788 7.85627 15.2882 7.90703C14.8991 8.01016 14.4866 7.9 14.2007 7.61641C14.0616 7.47623 13.9616 7.30211 13.9106 7.11134C13.8596 6.92057 13.8594 6.71978 13.9101 6.52891V6.52891Z" />
    <path d="M11.1703 18.578C11.1304 18.5734 11.0906 18.5546 11.0601 18.5241L3.22732 10.6913C3.19243 10.6561 3.17285 10.6085 3.17285 10.5589C3.17285 10.5093 3.19243 10.4617 3.22732 10.4265L12.0914 1.56242C12.1195 1.53196 12.1594 1.51321 12.2015 1.50852H12.1922C12.1383 1.50383 12.082 1.52258 12.0445 1.56242L3.18045 10.4265C3.14555 10.4617 3.12598 10.5093 3.12598 10.5589C3.12598 10.6085 3.14555 10.6561 3.18045 10.6913L11.0133 18.5241C11.0554 18.5687 11.114 18.5851 11.1703 18.578V18.578Z" />
    <path d="M20.8524 12.6529L19.9243 11.7271C19.8891 11.6922 19.8415 11.6726 19.7919 11.6726C19.7423 11.6726 19.6947 11.6922 19.6594 11.7271L11.1751 20.1951L5.60632 14.6404C5.57108 14.6055 5.5235 14.5859 5.4739 14.5859C5.42431 14.5859 5.37672 14.6055 5.34148 14.6404L4.41336 15.5662C4.37846 15.6014 4.35889 15.649 4.35889 15.6986C4.35889 15.7482 4.37846 15.7958 4.41336 15.831L10.1134 21.5216L11.0415 22.4474C11.1141 22.5201 11.2337 22.5201 11.3063 22.4474L20.8524 12.9177C20.9251 12.8451 20.9251 12.7255 20.8524 12.6529V12.6529ZM15.2884 7.90678C15.479 7.85602 15.6529 7.75582 15.7924 7.61631C15.9319 7.47679 16.0321 7.30291 16.0829 7.11225C16.186 6.72319 16.0759 6.31069 15.7923 6.02475C15.6526 5.88493 15.4785 5.78443 15.2875 5.7334C15.0966 5.68237 14.8956 5.68262 14.7048 5.73413C14.5141 5.78489 14.3402 5.88509 14.2007 6.02461C14.0612 6.16412 13.961 6.338 13.9102 6.52866C13.8071 6.91772 13.9173 7.33022 14.2009 7.61616C14.341 7.75524 14.5151 7.85524 14.7059 7.90622C14.8967 7.95721 15.0975 7.9574 15.2884 7.90678V7.90678Z" />
    <path d="M3.22732 10.4258C3.19243 10.461 3.17285 10.5086 3.17285 10.5582C3.17285 10.6078 3.19243 10.6554 3.22732 10.6906L11.0601 18.5234C11.0906 18.5539 11.1281 18.5727 11.1703 18.5773C11.2265 18.5844 11.2828 18.5656 11.325 18.5234L20.189 9.65703C20.2289 9.61719 20.2476 9.56328 20.2429 9.50938L19.6453 2.44531C19.6289 2.2625 19.4859 2.11953 19.3031 2.10312L12.239 1.50781H12.2015C12.1594 1.5125 12.1219 1.53125 12.0914 1.56172L3.22732 10.4258ZM12.7922 3.24922L18.0562 3.69453L18.5015 8.95859L11.1914 16.2664L5.48201 10.5594L12.7922 3.24922Z" />
  </svg>
);

export const AtPointOfInterest: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17.25,6.75A5.24,5.24,0,0,1,13,11.9v5.35H11V11.9a5.25,5.25,0,1,1,6.26-5.15Z" />
    <path d="M4.12,21.89v-2H19.88v2Z" />
  </svg>
);

export const AtRoom: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M22.21,11.29l-9.5-9.5a1,1,0,0,0-1.42,0L6.58,6.51l0,0,0,0L1.79,11.29a1,1,0,0,0,0,1.42l4.72,4.72,0,0,0,0,4.72,4.72a1,1,0,0,0,1.42,0l9.5-9.5A1,1,0,0,0,22.21,11.29ZM12,3.93l3.33,3.32L12,10.58,8.67,7.25ZM3.93,12,7.25,8.67,10.58,12,7.25,15.33ZM12,20.07,8.67,16.75,12,13.42l3.33,3.33Zm4.75-4.74L13.42,12l3.33-3.33L20.08,12Z" />
  </svg>
);

export const AtTraffic: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M20.49,3.79a1.18,1.18,0,0,0-.44-.57A1.2,1.2,0,0,0,19.37,3H12.63a1.2,1.2,0,0,0-.68.22,1.18,1.18,0,0,0-.44.57L10.7,6h2l.39-1.16a.51.51,0,0,1,.48-.34h4.88a.51.51,0,0,1,.48.34L20,8H16l1.85,4.78H20.2v.61a.58.58,0,0,0,.18.43.56.56,0,0,0,.42.18h.6a.56.56,0,0,0,.42-.18.58.58,0,0,0,.18-.43V7.89ZM19,11a1,1,0,1,1,1-1A1,1,0,0,1,19,11Z" />
    <path d="M15,8a1.51,1.51,0,0,0-.59-.73A1.6,1.6,0,0,0,13.49,7h-8a1.6,1.6,0,0,0-.91.28A1.51,1.51,0,0,0,4,8L2,13.22v7a.75.75,0,0,0,.23.55A.81.81,0,0,0,2.8,21h.8a.81.81,0,0,0,.57-.23.75.75,0,0,0,.23-.55v-.78H14.6v.78a.75.75,0,0,0,.23.55.81.81,0,0,0,.57.23h.8a.81.81,0,0,0,.57-.23.75.75,0,0,0,.23-.55v-7ZM6,17.5A1.5,1.5,0,1,1,7.5,16,1.5,1.5,0,0,1,6,17.5Zm7,0A1.5,1.5,0,1,1,14.5,16,1.5,1.5,0,0,1,13,17.5ZM4,13.22,5.56,9.15a1,1,0,0,1,.93-.65h6a1,1,0,0,1,.93.65L15,13.22Z" />
  </svg>
);

export const AtTrafficHigh: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M11.62,2.73l.17.46a2,2,0,0,0-1,1.11l-.2.53-.38-1.12a.51.51,0,0,0-.47-.34H5.29a.51.51,0,0,0-.47.34l-1,2.87H9.91l-.35.92H5.14A2,2,0,0,0,3.29,8.73L2,11.74a.54.54,0,0,1,0-.22v-5L3.38,2.73a1.08,1.08,0,0,1,.41-.53A1.07,1.07,0,0,1,4.41,2h6.18a1.07,1.07,0,0,1,.62.2A1.08,1.08,0,0,1,11.62,2.73Z" />
    <path d="M20.49,4.79a1.18,1.18,0,0,0-.44-.57A1.2,1.2,0,0,0,19.37,4H12.63a1.2,1.2,0,0,0-.68.22,1.18,1.18,0,0,0-.44.57l-1,2.71h2l.57-1.66a.51.51,0,0,1,.48-.34h4.88a.51.51,0,0,1,.48.34L20,9H15.82l2,4.78h2.4v.61a.58.58,0,0,0,.18.43.56.56,0,0,0,.42.18h.6a.56.56,0,0,0,.42-.18.58.58,0,0,0,.18-.43V8.89ZM19,12a1,1,0,1,1,1-1A1,1,0,0,1,19,12Z" />
    <path d="M15,9.51a1.51,1.51,0,0,0-.59-.73,1.6,1.6,0,0,0-.91-.28h-8a1.6,1.6,0,0,0-.91.28A1.51,1.51,0,0,0,4,9.51L2,14.22v7a.75.75,0,0,0,.23.55A.81.81,0,0,0,2.8,22h.8a.81.81,0,0,0,.57-.23.75.75,0,0,0,.23-.55v-.78H14.6v.78a.75.75,0,0,0,.23.55.81.81,0,0,0,.57.23h.8a.81.81,0,0,0,.57-.23.75.75,0,0,0,.23-.55v-7Zm-9,9A1.5,1.5,0,1,1,7.5,17,1.5,1.5,0,0,1,6,18.5Zm7,0A1.5,1.5,0,1,1,14.5,17,1.5,1.5,0,0,1,13,18.5ZM4,14.22l1.53-3.61A1,1,0,0,1,6.46,10h6.08a1,1,0,0,1,.92.61L15,14.22Z" />
  </svg>
);

export const AtTrafficLow: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M19.49,5.3a2,2,0,0,0-.74-.94A1.93,1.93,0,0,0,17.61,4H6.39a1.93,1.93,0,0,0-1.14.36,2,2,0,0,0-.74.94L2,12v9a1,1,0,0,0,1,1H4a1,1,0,0,0,1-1V20H19v1a1,1,0,0,0,1,1h1a1,1,0,0,0,1-1V12ZM7.56,16.56A1.5,1.5,0,1,1,8,15.5,1.5,1.5,0,0,1,7.56,16.56Zm11,0A1.5,1.5,0,1,1,19,15.5,1.5,1.5,0,0,1,18.56,16.56ZM4.14,12,6.39,6H17.61l2.25,6Z" />
  </svg>
);

export const AtTransportation: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17.5,17H19V4.5A2.5,2.5,0,0,0,16.5,2h-9A2.5,2.5,0,0,0,5,4.5V17H6.5L4,22H6l.83-1.75H17.17L18,22h2ZM7,14.25A1.25,1.25,0,1,1,8.25,15.5,1.25,1.25,0,0,1,7,14.25Zm.72,4.25L8.49,17h7l.77,1.5Zm8-3A1.25,1.25,0,1,1,17,14.25,1.25,1.25,0,0,1,15.75,15.5Zm1.25-5a.83.83,0,0,1-.83.83H7.83A.83.83,0,0,1,7,10.5v-6a.83.83,0,0,1,.83-.83h8.34A.83.83,0,0,1,17,4.5Z" />
    <path d="M2,6H4v5H2Z" />
    <path d="M22,6H20v5h2Z" />
  </svg>
);

export const AtWeather: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M14.6367 2V3V3.81818V4.81818H12.6367V3.81818V3V2H14.6367ZM19.5796 5.83455L20.2867 5.12744L18.8725 3.71323L18.1654 4.42034L17.5927 4.99306L16.8856 5.70017L18.2998 7.11438L19.0069 6.40728L19.5796 5.83455ZM20.1816 9.36377H19.1816V11.3638H20.1816H20.9998H21.9998V9.36377H20.9998H20.1816ZM9.10652 4.42033L8.39941 3.71323L6.9852 5.12744L7.69231 5.83455L8.26503 6.40728L8.97214 7.11438L10.3864 5.70017L9.67925 4.99306L9.10652 4.42033ZM2 15.2727C2 11.5295 4.98408 8.54541 8.72727 8.54541C8.9633 8.54541 9.19704 8.55783 9.4278 8.58208C10.1478 7.05602 11.7005 6 13.4998 6C15.9851 6 17.9998 8.01472 17.9998 10.5C17.9998 10.9852 17.923 11.4524 17.7809 11.8902C20.1985 12.2955 22 14.3636 22 16.909C22 19.7522 19.7523 22 16.9091 22H8.72727C4.98408 22 2 19.0159 2 15.2727ZM8.72727 10.5454C6.08865 10.5454 4 12.6341 4 15.2727C4 17.9113 6.08865 20 8.72727 20H16.9091C18.6477 20 20 18.6477 20 16.909C20 15.1704 18.6477 13.8181 16.9091 13.8181C16.1826 13.8181 15.4317 14.1305 14.9494 14.5525L13.7391 15.6116L13.3247 14.0576C12.7862 12.0382 10.8897 10.5454 8.72727 10.5454Z" />
  </svg>
);

export const AtZone: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M2,2H8.25V4H4V8.25H2Z" />
    <path d="M12,15.75A3.75,3.75,0,1,0,8.25,12,3.75,3.75,0,0,0,12,15.75Z" />
    <path d="M15.75,4H20V8.25h2V2H15.75Z" />
    <path d="M2,15.75H4V20H8.25v2H2Z" />
    <path d="M20,15.75V20H15.75v2H22V15.75Z" />
  </svg>
);

export const AtFavorite: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.001 4.529a5.998 5.998 0 0 1 8.242.228 6 6 0 0 1 .236 8.236l-8.48 8.492-8.478-8.492a6 6 0 0 1 8.48-8.464Z" />
  </svg>
);

export const AtInsights: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12.875 9.81818H19L11.125 24V14.1818H5L12.875 0V9.81818Z" />
  </svg>
);

export const AtUtilization: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4.257 5.67101L6.394 7.80801C5.33417 9.22533 4.85053 10.9915 5.0404 12.751C5.23027 14.5106 6.07957 16.1329 7.41736 17.2915C8.75516 18.45 10.4821 19.0589 12.2508 18.9955C14.0194 18.9321 15.6983 18.2011 16.9497 16.9497C18.2011 15.6983 18.9321 14.0194 18.9955 12.2508C19.0589 10.4822 18.45 8.75517 17.2914 7.41737C16.1329 6.07958 14.5105 5.23028 12.751 5.04041C10.9915 4.85054 9.22532 5.33418 7.808 6.39401L5.67 4.25701C7.45476 2.79399 9.69223 1.9962 12 2.00001C17.523 2.00001 22 6.47701 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 9.59901 2.846 7.39501 4.257 5.67101ZM7.828 9.24301L12 13.414L13.414 12L9.243 7.82801C10.2767 7.14474 11.5272 6.86964 12.7522 7.05602C13.9772 7.24241 15.0894 7.87701 15.8731 8.83679C16.6567 9.79658 17.0561 11.0132 16.9938 12.2507C16.9315 13.4882 16.4118 14.6585 15.5357 15.5347C14.6595 16.4108 13.4892 16.9305 12.2517 16.9928C11.0142 17.0551 9.79757 16.6557 8.83779 15.8721C7.878 15.0884 7.2434 13.9762 7.05701 12.7512C6.87063 11.5262 7.14573 10.2757 7.829 9.24201L7.828 9.24301Z" />
  </svg>
);

export const AtChevronDown: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 12.6659L15.1113 9.55469L16 10.4434L12 14.4434L8 10.4434L8.88875 9.55469L12 12.6659Z" />
  </svg>
);

export const AtCopyToClipboard: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M20 2H10c-1.103 0-2 .897-2 2v4H4c-1.103 0-2 .897-2 2v10c0 1.103.897 2 2 2h10c1.103 0 2-.897 2-2v-4h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM4 20V10h10l.002 10H4zm16-6h-4v-4c0-1.103-.897-2-2-2h-4V4h10v10z" />
    <path d="M6 12h6v2H6zm0 4h6v2H6z" />
  </svg>
);

// Weather icons

export const AtPrecipitation: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M4.70607 10.8302C6.10666 11.2055 7.95972 10.2786 8.57442 7.98448C9.14059 5.87152 7.29426 1.17099 7.29426 1.17099C7.29426 1.17099 3.34503 4.3186 2.77887 6.43157C2.16417 8.72564 3.30548 10.455 4.70607 10.8302Z" />
  </svg>
);

export const AtCloudRain: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path
      d="M7.39062 14.7051H15.9287C18.2529 14.7051 20.0029 12.9414 20.0029 10.7266C20.0029 8.46387 18.1914 6.76172 15.8193 6.76172C14.9922 5.1416 13.4541 4 11.3076 4C8.64844 4 6.51562 6.0166 6.23535 8.56641C4.96387 8.96289 4 10.0771 4 11.5742C4 13.3174 5.28516 14.7051 7.39062 14.7051Z"
      fill={"#FFFFFF"}
    />
    <path
      d="M13.0578 18.0697L13.9328 16.5521C14.0695 16.3197 14.0012 16.0394 13.7687 15.9027C13.5363 15.7728 13.2697 15.848 13.1262 16.0941L12.2512 17.6185C12.1144 17.8372 12.176 18.1175 12.4152 18.2542C12.6477 18.3841 12.9279 18.2952 13.0578 18.0697ZM14.6574 20.0111L16.6603 16.5452C16.7902 16.3197 16.7287 16.0462 16.5031 15.9095C16.2707 15.7728 15.9973 15.848 15.8605 16.0872L13.8644 19.5462C13.7346 19.7786 13.7893 20.0521 14.0285 20.1888C14.2541 20.3255 14.5275 20.2435 14.6574 20.0111ZM7.62324 18.0765L8.49824 16.5589C8.63496 16.3265 8.5666 16.0462 8.33417 15.9095C8.09492 15.7728 7.82831 15.8548 7.6916 16.0941L6.80976 17.6185C6.67988 17.8441 6.73456 18.1175 6.97382 18.2611C7.21308 18.3909 7.49335 18.3021 7.62324 18.0765ZM9.21601 20.0179L11.2189 16.5521C11.3557 16.3265 11.2873 16.0462 11.0617 15.9095C10.8361 15.7728 10.5559 15.8548 10.4191 16.0941L8.42304 19.5462C8.29316 19.7786 8.35468 20.0589 8.5871 20.1956C8.81269 20.3255 9.08613 20.2435 9.21601 20.0179Z"
      fill={"#5EC8F4"}
    />
  </svg>
);

// Category icons

export const AtFoodBev: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M21 2V22H19V14H16V7C16 5.67392 16.5268 4.40215 17.4645 3.46447C18.4021 2.52678 19.6739 2 21 2V2ZM9 13.9V22H7V13.9C5.87081 13.6691 4.85599 13.0554 4.12714 12.1625C3.3983 11.2697 3.00014 10.1526 3 9V3H5V10H7V3H9V10H11V3H13V9C12.9999 10.1526 12.6017 11.2697 11.8729 12.1625C11.144 13.0554 10.1292 13.6691 9 13.9V13.9Z" />
  </svg>
);

export const AtArchitecture: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M18.9009 10C19.2959 10.6856 19.9459 11.1872 20.7092 11.3957C21.4724 11.6042 22.2872 11.5026 22.9759 11.113C23.0569 11.8366 22.9101 12.5675 22.5561 13.2039C22.2021 13.8402 21.6584 14.3502 21.0009 14.663L20.9999 21H14.9999V19C15.0006 18.2191 14.6968 17.4687 14.1531 16.9082C13.6093 16.3477 12.8685 16.0213 12.0879 15.9984C11.3073 15.9755 10.5486 16.2578 9.97289 16.7855C9.39719 17.3131 9.04992 18.0444 9.00486 18.824L8.99986 19V21H2.99986V14.664C2.34101 14.3517 1.79614 13.8414 1.44135 13.2044C1.08655 12.5675 0.939547 11.8356 1.02086 11.111C1.7095 11.5014 2.52454 11.6036 3.28827 11.3955C4.052 11.1874 4.70247 10.6858 5.09786 10H18.9009ZM17.0359 3C17.1074 3.4961 17.2846 3.97105 17.5556 4.39272C17.8266 4.81438 18.185 5.17293 18.6065 5.4441C19.0281 5.71527 19.5029 5.89273 19.999 5.96448C20.4951 6.03624 21.0008 6.00061 21.4819 5.86C21.3962 6.68858 21.0179 7.45945 20.4149 8.03408C19.8118 8.60872 19.0236 8.94943 18.1919 8.995L17.9999 9H5.99986C5.13383 9.00014 4.29851 8.6792 3.65536 8.09923C3.01222 7.51925 2.60694 6.72143 2.51786 5.86C2.99896 6.00061 3.50466 6.03624 4.00072 5.96448C4.49679 5.89273 4.97166 5.71527 5.3932 5.4441C5.81475 5.17293 6.17313 4.81438 6.44411 4.39272C6.71508 3.97105 6.89233 3.4961 6.96386 3H17.0359Z" />
  </svg>
);

export const AtSecurity: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M12 1L20.217 2.826C20.674 2.928 21 3.333 21 3.802V13.789C21 15.795 19.997 17.669 18.328 18.781L12 23L5.672 18.781C4.002 17.668 3 15.795 3 13.79V3.802C3 3.333 3.326 2.928 3.783 2.826L12 1ZM16.452 8.222L11.502 13.171L8.674 10.343L7.26 11.757L11.503 16L17.867 9.636L16.452 8.222V8.222Z" />
  </svg>
);

export const AtFacilities: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.9901 1.5H10.9651V22.5H12.9901V1.5ZM5.4375 8.0625C6.88725 8.0625 8.0625 6.88725 8.0625 5.4375C8.0625 3.98775 6.88725 2.8125 5.4375 2.8125C3.98775 2.8125 2.8125 3.98775 2.8125 5.4375C2.8125 6.88725 3.98775 8.0625 5.4375 8.0625ZM2.65327 10.5283C3.39169 9.78984 4.39321 9.375 5.4375 9.375C6.48179 9.375 7.48331 9.78984 8.22173 10.5283C8.96016 11.2667 9.375 12.2682 9.375 13.3125V17.25H8.0625V21.1875H2.8125V17.25H1.5V13.3125C1.5 12.2682 1.91484 11.2667 2.65327 10.5283ZM18.5625 8.0625C20.0122 8.0625 21.1875 6.88725 21.1875 5.4375C21.1875 3.98775 20.0122 2.8125 18.5625 2.8125C17.1128 2.8125 15.9375 3.98775 15.9375 5.4375C15.9375 6.88725 17.1128 8.0625 18.5625 8.0625ZM21.3467 10.5283C22.0852 11.2667 22.5 12.2682 22.5 13.3125V17.25H21.1875V21.1875H15.9375V17.25H14.625V13.3125C14.625 12.2682 15.0398 11.2667 15.7783 10.5283C16.5167 9.78984 17.5182 9.375 18.5625 9.375C19.6068 9.375 20.6083 9.78984 21.3467 10.5283Z"
    />
  </svg>
);

export const AtGreenSpace: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M22 7V9.5C22 13.09 19.09 16 15.5 16H13V21H11V14L11.019 13C11.274 9.644 14.079 7 17.5 7H22ZM6 3C9.092 3 11.716 5.005 12.643 7.786C11.143 9.061 10.153 10.914 10.016 13H9C5.134 13 2 9.866 2 6V3H6Z" />
  </svg>
);

export const AtRetail: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path d="M22.0001 20V22H2.00012V20H3.00012V13.242C2.38449 12.8311 1.87984 12.2745 1.53094 11.6218C1.18205 10.969 0.999714 10.2402 1.00012 9.5C1.00012 8.673 1.22412 7.876 1.63312 7.197L4.34512 2.5C4.43289 2.34798 4.55912 2.22175 4.71114 2.13398C4.86315 2.04621 5.03559 2 5.21112 2H18.7901C18.9657 2 19.1381 2.04621 19.2901 2.13398C19.4421 2.22175 19.5684 2.34798 19.6561 2.5L22.3581 7.182C22.9547 8.17287 23.1464 9.35553 22.8935 10.4841C22.6406 11.6127 21.9625 12.6005 21.0001 13.242V20H22.0001ZM5.78912 4L3.35612 8.213C3.1197 8.79714 3.1126 9.44903 3.33625 10.0382C3.5599 10.6273 3.9978 11.1103 4.5623 11.3904C5.12681 11.6705 5.77627 11.7271 6.3807 11.5488C6.98514 11.3706 7.49996 10.9706 7.82212 10.429C8.15712 9.592 9.34212 9.592 9.67812 10.429C9.86342 10.8934 10.1837 11.2916 10.5976 11.5721C11.0116 11.8526 11.5001 12.0025 12.0001 12.0025C12.5001 12.0025 12.9887 11.8526 13.4026 11.5721C13.8165 11.2916 14.1368 10.8934 14.3221 10.429C14.6571 9.592 15.8421 9.592 16.1781 10.429C16.3079 10.7484 16.5024 11.0376 16.7493 11.2783C16.9962 11.519 17.2902 11.706 17.6129 11.8275C17.9355 11.9491 18.2798 12.0026 18.6242 11.9847C18.9685 11.9668 19.3054 11.8778 19.6137 11.7234C19.922 11.569 20.1951 11.3525 20.4157 11.0875C20.6363 10.8225 20.7997 10.5148 20.8956 10.1836C20.9915 9.85238 21.0179 9.50493 20.9731 9.16305C20.9282 8.82118 20.8132 8.49227 20.6351 8.197L18.2101 4H5.79012H5.78912Z" />
  </svg>
);

export const AtTransit: React.FC<Props> = (props) => (
  <svg viewBox="0 0 12 12" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.49988 2H16.4999C17.8782 2 18.9999 3.12167 18.9999 4.5V17H17.4997L19.9997 22H17.9997L17.1719 20.2503L6.82705 20.2502L5.99966 21.9999H3.99966L6.49959 17H4.99988V4.5C4.99988 3.12167 6.12154 2 7.49988 2ZM8.48618 17L7.71587 18.5004L16.2831 18.5005L15.5129 17H8.48618ZM16.1665 11.3333C16.6265 11.3333 16.9999 10.96 16.9999 10.5V4.5C16.9999 4.04 16.6265 3.66667 16.1665 3.66667H7.83321C7.37321 3.66667 6.99988 4.04 6.99988 4.5V10.5C6.99988 10.96 7.37321 11.3333 7.83321 11.3333H16.1665ZM8.24988 15.5C8.94023 15.5 9.49988 14.9404 9.49988 14.25C9.49988 13.5596 8.94023 13 8.24988 13C7.55952 13 6.99988 13.5596 6.99988 14.25C6.99988 14.9404 7.55952 15.5 8.24988 15.5ZM16.9999 14.25C16.9999 14.9404 16.4402 15.5 15.7499 15.5C15.0595 15.5 14.4999 14.9404 14.4999 14.25C14.4999 13.5596 15.0595 13 15.7499 13C16.4402 13 16.9999 13.5596 16.9999 14.25ZM1.99988 6H3.99988V11H1.99988V6ZM21.9999 6H19.9999V11H21.9999V6Z"
    />
  </svg>
);

export const AtAdd: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z" />
  </svg>
);

export const AtSubtract: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4 12C4 11.4477 4.44772 11 5 11H19C19.5523 11 20 11.4477 20 12C20 12.5523 19.5523 13 19 13H5C4.44772 13 4 12.5523 4 12Z" />
  </svg>
);

// Status (Complete or Incomplete)

export const AtComplete: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.5 13.125L16.125 7.5L18 9.375L10.5 16.875L6 12.375L7.875 10.5L10.5 13.125Z"
      fill="#79c22a"
    />
  </svg>
);

export const AtIncomplete: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM13.395 13.0829L13.7089 5.63672H10.3282L10.6421 13.0829H13.395ZM10.8172 17.2084C11.1512 17.4939 11.5517 17.6367 12.0185 17.6367C12.3164 17.6367 12.592 17.5715 12.8456 17.441C13.0991 17.3071 13.3044 17.129 13.4614 16.9069C13.6224 16.6813 13.7049 16.4309 13.7089 16.1559C13.7049 15.754 13.5318 15.4103 13.1897 15.1247C12.8516 14.8391 12.4612 14.6963 12.0185 14.6963C11.5517 14.6963 11.1512 14.8391 10.8172 15.1247C10.4872 15.4103 10.3242 15.754 10.3282 16.1559C10.3242 16.5684 10.4872 16.9193 10.8172 17.2084Z"
      fill="#f59b00"
    />
  </svg>
);

// Settings Icons

// export const AtSpaces: React.FC<Props> = (props) => (
//   <svg viewBox="0 0 24 24" {...props}></svg>
// );

export const AtDevices: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M1 7.13258C4.11141 4.61194 7.99569 3.23921 12 3.24514C16.1676 3.24514 19.9947 4.7011 23 7.13258L21.1667 9.40164C18.5737 7.30135 15.3368 6.1577 12 6.16291C8.52785 6.16291 5.33775 7.3767 2.83333 9.40164L1 7.13258ZM4.05491 10.916C6.30218 9.09527 9.10775 8.10375 12 8.10809C15.0092 8.10809 17.7733 9.15946 19.9451 10.915L18.1118 13.185C16.383 11.7847 14.2248 11.0222 12 11.0259C9.68524 11.0259 7.55915 11.8351 5.88824 13.185L4.05491 10.915V10.916ZM7.11079 14.6993C8.49366 13.5788 10.2201 12.9685 12 12.971C13.8518 12.971 15.5529 13.6178 16.8892 14.6984L15.0559 16.9684C14.1914 16.2683 13.1124 15.8871 12 15.8888C10.8876 15.8871 9.80856 16.2683 8.94412 16.9684L7.11079 14.6984V14.6993ZM10.1667 18.4827C10.6851 18.0623 11.3325 17.8332 12 17.834C12.6944 17.834 13.3325 18.0771 13.8333 18.4817L12 20.7518L10.1667 18.4817V18.4827Z" />
  </svg>
);

export const AtIntegrations: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M16.4001 16.4V12L21.9001 17.5L16.4001 23V18.6H3.2001V16.4H16.4001ZM7.6001 1V5.3989L20.8001 5.4V7.6H7.6001V12L2.1001 6.5L7.6001 1Z" />
  </svg>
);

export const AtUsers: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M12 11C13.3261 11 14.5979 11.5268 15.5355 12.4645C16.4732 13.4021 17 14.6739 17 16V22H7V16C7 14.6739 7.52678 13.4021 8.46447 12.4645C9.40215 11.5268 10.6739 11 12 11ZM5.288 14.006C5.12886 14.5428 5.03485 15.0968 5.008 15.656L5 16V22H2V17.5C1.9998 16.6376 2.31803 15.8054 2.89363 15.1632C3.46924 14.521 4.2617 14.1139 5.119 14.02L5.289 14.006H5.288ZM18.712 14.006C19.6019 14.0602 20.4376 14.452 21.0486 15.1012C21.6596 15.7505 21.9999 16.6084 22 17.5V22H19V16C19 15.307 18.9 14.638 18.712 14.006ZM5.5 8C6.16304 8 6.79893 8.26339 7.26777 8.73223C7.73661 9.20107 8 9.83696 8 10.5C8 11.163 7.73661 11.7989 7.26777 12.2678C6.79893 12.7366 6.16304 13 5.5 13C4.83696 13 4.20107 12.7366 3.73223 12.2678C3.26339 11.7989 3 11.163 3 10.5C3 9.83696 3.26339 9.20107 3.73223 8.73223C4.20107 8.26339 4.83696 8 5.5 8V8ZM18.5 8C19.163 8 19.7989 8.26339 20.2678 8.73223C20.7366 9.20107 21 9.83696 21 10.5C21 11.163 20.7366 11.7989 20.2678 12.2678C19.7989 12.7366 19.163 13 18.5 13C17.837 13 17.2011 12.7366 16.7322 12.2678C16.2634 11.7989 16 11.163 16 10.5C16 9.83696 16.2634 9.20107 16.7322 8.73223C17.2011 8.26339 17.837 8 18.5 8V8ZM12 2C13.0609 2 14.0783 2.42143 14.8284 3.17157C15.5786 3.92172 16 4.93913 16 6C16 7.06087 15.5786 8.07828 14.8284 8.82843C14.0783 9.57857 13.0609 10 12 10C10.9391 10 9.92172 9.57857 9.17157 8.82843C8.42143 8.07828 8 7.06087 8 6C8 4.93913 8.42143 3.92172 9.17157 3.17157C9.92172 2.42143 10.9391 2 12 2V2Z" />
  </svg>
);

export const AtUpload: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4 19H20V12H22V20C22 20.2652 21.8946 20.5196 21.7071 20.7071C21.5196 20.8946 21.2652 21 21 21H3C2.73478 21 2.48043 20.8946 2.29289 20.7071C2.10536 20.5196 2 20.2652 2 20V12H4V19ZM13 9V16H11V9H6L12 3L18 9H13Z" />
  </svg>
);

export const AtDelete: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M4 8H20V21C20 21.2652 19.8946 21.5196 19.7071 21.7071C19.5196 21.8946 19.2652 22 19 22H5C4.73478 22 4.48043 21.8946 4.29289 21.7071C4.10536 21.5196 4 21.2652 4 21V8ZM7 5V3C7 2.73478 7.10536 2.48043 7.29289 2.29289C7.48043 2.10536 7.73478 2 8 2H16C16.2652 2 16.5196 2.10536 16.7071 2.29289C16.8946 2.48043 17 2.73478 17 3V5H22V7H2V5H7ZM9 4V5H15V4H9ZM9 12V18H11V12H9ZM13 12V18H15V12H13Z" />
  </svg>
);

export const AtSave: React.FC<Props> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path d="M17.5556 2H4.22222C2.98889 2 2 3 2 4.22222V19.7778C2 21 2.98889 22 4.22222 22H19.7778C21 22 22 21 22 19.7778V6.44444L17.5556 2ZM12 19.7778C10.1556 19.7778 8.66667 18.2889 8.66667 16.4444C8.66667 14.6 10.1556 13.1111 12 13.1111C13.8444 13.1111 15.3333 14.6 15.3333 16.4444C15.3333 18.2889 13.8444 19.7778 12 19.7778ZM15.3333 8.66667H4.22222V4.22222H15.3333V8.66667Z" />
  </svg>
);
